import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Hand, Loader2, Send, Users } from "lucide-react";
import { useTenantRoles, useTenantSlaLadder, useSaveTenantSlaLadder } from "../../lib/api";
import { DEFAULT_ROLE_TERMS } from "../../lib/TenantConfigContext";
import { UNITS, absoluteSeconds, ladderProblem, splitDelay, waitProblem, withWaits } from "../../lib/slaLadder";
import {
  gatedStepKeys,
  isQuestion,
  questionFromSettings,
  questionProblem,
  settingsFromQuestion,
  stepAfterQuestion,
} from "../../lib/slaQuestion";
import SlaConditionNode, { SlaEndNode } from "./SlaConditionNode";
import { Edge, LadderCanvas, NodeShell, StartNode } from "./SlaNodes";
import { FirstMessageNode, ReplyNode } from "@packs/automotive/web/pages/SuperAdmin/PreIfrFirstMessage";
import {
  DetailHeader,
  GhostButton,
  PrimaryButton,
  SecondaryButton,
  Select,
  Toggle,
  inputClass,
} from "./primitives";

let tempId = 0;
const newRung = (seconds, role) => ({
  key: `new-${tempId++}`,
  ...splitDelay(seconds),
  target_type: role ? "role" : "group",
  target_role: role || null,
  template_name: "",
  label: "",
  enabled: true,
});

// A customer step in a ladder that asks questions: "has your issue been resolved?".
const newQuestion = (seconds) => ({
  ...newRung(seconds, null),
  target_type: "customer",
  target_role: null,
  qs: questionFromSettings(),
});

const TARGET_LABELS = {
  group: "The group",
  customer: "The customer",
  pending_members: "Whoever has not joined",
};

// Stored steps as the editor holds them: in the ladder's order, each with the wait after the step before it.
const fromStored = (items, conditional) => withWaits(items, (sorted) => (conditional ? gatedStepKeys(sorted) : new Set()));

const toRungs = (data) => {
  const conditional = !!data?.meta?.conditional;
  return fromStored(
    (data?.rungs || []).map((r) => ({
      ...r,
      key: `db-${r.id}`,
      // What a question step holds: how long it waits for an answer, its reminders, and so on.
      ...(conditional && r.target_type === "customer" ? { qs: questionFromSettings(r.settings) } : {}),
    })),
    conditional,
  );
};

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

// `firstMessage` (from useFirstMessage) puts the Pre-IFR journey's first message and the customer's reply in front
// of the steps, and makes Save, Discard and the unsaved mark cover it too.
export default function SlaEditor({ tenantId, ladder = "response", title, firstMessage = null }) {
  const fm = firstMessage && firstMessage.ready ? firstMessage : null;
  const { data, isLoading } = useTenantSlaLadder(tenantId, ladder);
  const save = useSaveTenantSlaLadder(tenantId, ladder);
  // The selected brand's own roles, by their full names ("Workshop Manager",
  // not "WM"). Super Admin sits outside the brand's config, so the names come
  // from this brand's Roles & access list, not from whoever is signed in.
  const { data: rolesData } = useTenantRoles(tenantId);
  const roleName = (code) =>
    (rolesData?.rows || []).find((r) => r.code === code)?.label || DEFAULT_ROLE_TERMS[code]?.label || code;

  // What a node is called: who it reaches, in the brand's own role names.
  const nodeTitle = (r, asks) =>
    asks
      ? "Ask the customer"
      : r.target_type === "role"
        ? `Alert ${roleName(r.target_role)}`
        : r.target_type === "group"
          ? "Post in the group"
          : r.target_type === "customer"
            ? "Message the customer"
            : TARGET_LABELS[r.target_type] || r.target_type;

  const [enabled, setEnabled] = useState(false);
  const [rungs, setRungs] = useState([]);
  const baseline = useMemo(() => ({ enabled: data?.enabled ?? false, rungs: toRungs(data) }), [data]);

  useEffect(() => {
    setEnabled(baseline.enabled);
    setRungs(baseline.rungs);
  }, [baseline]);

  const roles = data?.target_roles || [];
  // Which targets this ladder allows. A join ladder has no group option: the
  // premise is that people are not in the group yet.
  const targets = data?.meta?.targets || ["group", "role"];
  const nonRoleTargets = targets.filter((t) => t !== "role");
  // A ladder that asks questions: its customer steps are questions with a condition after them.
  const conditional = !!data?.meta?.conditional;

  // The ladder is in the order it was built, never sorted by the times typed: each step's delay is how long
  // it waits after the step before it, so a time can move the steps after it but never reorder anything.
  const ordered = rungs;

  // The staff steps that wait for a "no" have no timer of their own.
  const gated = useMemo(() => (conditional ? gatedStepKeys(ordered) : new Set()), [conditional, ordered]);

  const dirty = enabled !== baseline.enabled || JSON.stringify(rungs) !== JSON.stringify(baseline.rungs) || !!fm?.changed;

  const update = (key, patch) => setRungs((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // New steps go on the end, five minutes after the one before.
  const addQuestion = () => setRungs((rs) => [...rs, newQuestion(300)]);

  const addStep = () => {
    // A new step goes to the brand's first role, or to the group when this
    // ladder cannot target a role.
    const role = targets.includes("role") ? roles[0] : null;
    const rung = newRung(300, role);
    setRungs((rs) => [...rs, role ? rung : { ...rung, target_type: nonRoleTargets[0] || "group" }]);
  };

  // The default ladder names roles every brand used to have; steps for a role
  // this brand does not have are left out rather than saved and refused.
  const useStarter = () =>
    setRungs(
      fromStored(
        (data?.starter || [])
          .filter((s) => s.target_type !== "role" || roles.includes(s.target_role))
          .map((s, i) => ({
            ...newRung(0, null),
            seconds: s.seconds,
            key: `starter-${i}`,
            target_type: s.target_type,
            target_role: s.target_role,
            label: s.label,
            ...(conditional && s.target_type === "customer" ? { qs: questionFromSettings(s.settings) } : {}),
          })),
        conditional,
      ),
    );

  const onSave = async () => {
    if (fm?.changed && fm.problem) {
      toast.error(fm.problem);
      return;
    }
    // What is stored is each step's wait added to the ones before it.
    const seconds = absoluteSeconds(ordered, gated);
    const problem = waitProblem(ordered, gated) || ladderProblem(ordered.map((r, i) => ({ ...r, ...splitDelay(seconds[i]) })));
    if (problem) {
      toast.error(problem);
      return;
    }
    if (conditional) {
      for (const [i, r] of ordered.entries()) {
        const bad = isQuestion(r) ? questionProblem(r.qs || questionFromSettings()) : "";
        if (bad) {
          toast.error(`Step ${i + 1}: ${bad}`);
          return;
        }
      }
    }
    try {
      await save.mutateAsync({
        enabled,
        rungs: ordered.map((r, i) => ({
          seconds: seconds[i],
          target_type: r.target_type,
          target_role: r.target_type === "role" ? r.target_role : null,
          template_name: (r.template_name || "").trim(),
          label: r.label || "",
          enabled: !!r.enabled,
          // Only a ladder that asks questions has step settings, and only on its questions.
          ...(conditional ? { settings: isQuestion(r) ? settingsFromQuestion(r.qs || questionFromSettings()) : {} } : {}),
        })),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
      return;
    }
    if (fm?.changed) {
      try {
        await fm.save();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "The steps were saved, but the first message could not be.");
        return;
      }
    }
    toast.success("Escalations saved.");
  };

  const onDiscard = () => {
    setEnabled(baseline.enabled);
    setRungs(baseline.rungs);
    fm?.reset();
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  return (
    <>
      <DetailHeader
        title={title}
        toggleLabel="Send escalations"
        status={enabled !== baseline.enabled ? "Not saved" : ""}
        checked={enabled}
        onChange={setEnabled}
      />
      <div className="flex flex-col gap-6">
        {!ordered.length && !fm ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-[#BBD4F1] px-4 py-10">
            <div className="text-[14px] font-medium text-[#111827]">No steps</div>
            <div className="flex items-center gap-2">
              <PrimaryButton onClick={useStarter}>Use default ladder</PrimaryButton>
              <SecondaryButton onClick={addStep}>Add step</SecondaryButton>
            </div>
          </div>
        ) : (
          <LadderCanvas>
            <StartNode label={fm ? "Job card arrives from Tata" : "Ladder starts"} />
            {fm && (
              <>
                <Edge />
                <FirstMessageNode fm={fm} />
                <Edge />
                <ReplyNode />
              </>
            )}
            {ordered.map((r, i) => {
              const active = enabled && r.enabled;
              const asks = conditional && isQuestion(r);
              const after = asks ? stepAfterQuestion(ordered, r) : null;
              const template = (r.template_name || "").trim();
              return (
                <Fragment key={r.key}>
                  <Edge dashed={gated.has(r.key) || (!!fm && i === 0)} />
                  <NodeShell
                    index={i}
                    question={asks}
                    off={!active}
                    title={nodeTitle(r, asks)}
                    icon={asks ? HelpCircle : r.target_type === "group" ? Users : Send}
                    summary={template ? `Template: ${template}` : "No template yet"}
                    missing={!template}
                    testId={`node-${r.key}`}
                    actions={
                      <div className="flex flex-none items-center gap-2">
                        <Toggle checked={!!r.enabled} onChange={(v) => update(r.key, { enabled: v })} label={`Step ${i + 1} active`} />
                        <button
                          type="button"
                          onClick={() => setRungs((rs) => rs.filter((x) => x.key !== r.key))}
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#6B7280] transition-colors duration-150 hover:bg-[#FEF2F2] hover:text-[#DC2626] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5]"
                          aria-label={`Remove step ${i + 1}`}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    }
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      {gated.has(r.key) ? (
                        <span
                          className="inline-flex items-center gap-[6px] rounded-full bg-[#FEF3F2] px-[10px] py-[3px] text-[12.5px] font-semibold text-[#B42318]"
                          data-testid={`gated-${r.key}`}
                        >
                          <Hand size={12} aria-hidden="true" />
                          When the customer answers No
                        </span>
                      ) : (
                        <>
                          <span className="text-[13px] font-medium text-[#374151]">After</span>
                          <input
                            type="number"
                            min="0"
                            className={`${inputClass} !w-[76px] text-right`}
                            value={r.amount}
                            onChange={(e) => update(r.key, { amount: e.target.value })}
                            aria-label={`Delay for step ${i + 1}`}
                          />
                          <Select
                            className="w-[94px]"
                            value={r.unit}
                            onChange={(e) => update(r.key, { unit: e.target.value })}
                            aria-label={`Delay unit for step ${i + 1}`}
                          >
                            {UNITS.map((u) => (
                              <option key={u.value} value={u.value}>
                                {u.label}
                              </option>
                            ))}
                          </Select>
                          <span className="text-[13px] text-[#6B7280]" data-testid={`since-${r.key}`}>
                            {i === 0 ? (fm ? "from the unhappy reply" : "from the start") : `from step ${i}`}
                          </span>
                        </>
                      )}
                      <span className="text-[13px] font-medium text-[#374151]">{asks ? "ask" : "send to"}</span>
                      <Select
                        className="w-[240px]"
                        value={r.target_type === "role" ? `role:${r.target_role}` : r.target_type}
                        onChange={(e) => {
                          const v = e.target.value;
                          update(
                            r.key,
                            v.startsWith("role:")
                              ? { target_type: "role", target_role: v.slice(5), qs: undefined }
                              : {
                                  target_type: v,
                                  target_role: null,
                                  // Aiming a step at the customer in this ladder makes it a question.
                                  qs: conditional && v === "customer" ? r.qs || questionFromSettings() : undefined,
                                },
                          );
                        }}
                        aria-label={`Recipient for step ${i + 1}`}
                      >
                        {nonRoleTargets.map((t) => (
                          <option key={t} value={t}>
                            {TARGET_LABELS[t] || t}
                          </option>
                        ))}
                        {targets.includes("role") &&
                          roles.map((role) => (
                            <option key={role} value={`role:${role}`}>
                              {roleName(role)}
                            </option>
                          ))}
                      </Select>
                    </div>
                    <input
                      className={inputClass}
                      value={r.template_name || ""}
                      placeholder={asks ? "Template that asks: has your issue been resolved?" : "Template name"}
                      onChange={(e) => update(r.key, { template_name: e.target.value })}
                      aria-label={`Template for step ${i + 1}`}
                    />
                  </NodeShell>
                  {asks && (
                    <>
                      <Edge />
                      <SlaConditionNode
                        id={r.key}
                        q={r.qs || questionFromSettings()}
                        onChange={(patch) => update(r.key, { qs: { ...(r.qs || questionFromSettings()), ...patch } })}
                        next={
                          after
                            ? {
                                position: ordered.findIndex((x) => x.key === after.key) + 1,
                                name: after.target_type === "role" ? roleName(after.target_role) : TARGET_LABELS[after.target_type] || after.target_type,
                              }
                            : null
                        }
                      />
                      {!after && (
                        <>
                          <Edge dashed />
                          <SlaEndNode
                            id={r.key}
                            q={r.qs || questionFromSettings()}
                            onChange={(patch) => update(r.key, { qs: { ...(r.qs || questionFromSettings()), ...patch } })}
                          />
                        </>
                      )}
                    </>
                  )}
                </Fragment>
              );
            })}

            <Edge dashed />
            <div className="flex flex-wrap items-center justify-center gap-2">
              {!ordered.length && <SecondaryButton onClick={useStarter}>Use default ladder</SecondaryButton>}
              <SecondaryButton onClick={addStep}>
                <PlusIcon /> Add step
              </SecondaryButton>
              {conditional && (
                <SecondaryButton onClick={addQuestion}>
                  <PlusIcon /> Add question
                </SecondaryButton>
              )}
            </div>
          </LadderCanvas>
        )}

        <div className="flex items-center gap-3">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save changes
          </PrimaryButton>
          {dirty && <GhostButton onClick={onDiscard}>Discard</GhostButton>}
        </div>
      </div>
    </>
  );
}
